import type { ChatMessage, ChatThread, FileAttachment } from '../features/chat/chat.types';
import { isMeaninglessToolName, parseAiToolField } from '../utils/toolHistory';
import { parseApiDate } from '../utils/dateTime';
import { stripAreaContext } from '../utils/areaContext';

/* ── API response types ── */

interface RecentHistoryItem {
  id: {
    timestamp: number;
    creationTime: string;
  };
  userId: string;
  conversationId: string;
  question: string;
  created: string;
  hasNotification: boolean | null;
  // Present when this conversation was held with a Digital Workforce
  // (claude-managed) agent — see MomentumMyAMPRecentHistoryMongoDB.agent_id/
  // agent_name on the backend, serialized camelCase.
  agentId?: string | null;
  agentName?: string | null;
  // True when any turn of the conversation was spoken (ChatSource 7/8) — the
  // AMP API works it out per conversation.
  isVoice?: boolean;
  // The conversation's latest question, and how many turns it has.
  lastQuestion?: string | null;
  turns?: number;
  // When its latest turn was added, and when the user last opened it or added
  // to it (the later of the two) — the key a 'lastAccessed' listing is ordered
  // by. Absent from an AMP API that predates them.
  lastActivity?: string | null;
  lastAccessed?: string | null;
}

interface ConversationEntry {
  conversationId: string;
  chatId: string;
  question: string;
  created: string;
  aiService: string;
  title: string | null;
  tags: string | null;
  hasNotification: boolean;
  ampId: string | null;
  iconId: string | null;
}

interface ConversationEntry {
  companyId: number;
  conversationId: string;
  chatId: string;
  aI_Tool: string;
  question: string;
  response: string;
  pdfName: string;
  like: boolean;
  disLike: boolean;
  feedback: string;
  serializedJSONResponse: string;
  insuredId: string;
  policyId: string;
  pageURL: string[];
  imageURL: string[];
  videoURL: string[];
  responseStatusTable: string;
  aiGenImgURL: string | null;
  plotlyData: string | null;
  chartJsData?: string;
  aiToolName?: string[];
  aiToolPayload?: string[];
  aiToolResponse?: string[];
  // Files generated during the turn (claude-managed upload_session_file) —
  // same field names as the chat stream's terminal-answer artifacts entries.
  artifacts?: { filename?: string | null; app_url?: string | null; artifact_id?: string | null; tool_use_id?: string | null }[] | null;
  // Artifact-export flag persisted with the turn (claude-managed chats) — same
  // value as the live stream's terminal answer event, so reloaded
  // conversations re-render the Generate-artifact button.
  canGenerateArtifact?: boolean | null;
}

interface ConversationDetailResponse {
  momentumMyAMPHistoryConversation: ConversationEntry[];
  fileUrls: string[];
  aIService: string;
  isVoice?: boolean;
}

/* ── Helpers ── */

function getAuthHeaders(): Record<string, string> | null {
  const authToken = localStorage.getItem('authToken');
  if (!authToken) return null;
  return { Authorization: `Bearer ${authToken}` };
}

// Turn timestamp for display. Mirrors ChatContainer's formatMsgTimestamp so a
// reloaded turn shows the same "Aug 21, 2026 • 03:41 PM" shape as a live one.
// Returns '' when the backend sent no usable `created`, letting callers keep
// their existing fallback time.
function formatHistoryTimestamp(created?: string | null): string {
  const date = parseApiDate(created);
  if (!date) return '';
  const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return `${datePart} • ${timePart}`;
}

function extractChartJsFromText(text: string): string {
  const match = text.match(/\$\$ChartJSDataStart\$\$([\s\S]*?)\$\$ChartJSDataEnd\$\$/);
  return match ? match[1].trim() : '';
}

function normalizeAttachedFilesInQuestion(question: string): string {
  const lines = stripAreaContext(question || '').split('\n');
  const output: string[] = [];
  let inAttachedSection = false;

  for (const raw of lines) {
    const line = raw.trim();
    if (/^\[?Attached files\]?\s*:?$/i.test(line)) {
      inAttachedSection = true;
      output.push('Attached files:');
      continue;
    }
    if (!inAttachedSection) {
      output.push(raw);
      continue;
    }

    if (!line) {
      output.push(raw);
      inAttachedSection = false;
      continue;
    }

    const bullet = line.replace(/^[-*]\s*/, '').trim();
    const withUrl = bullet.match(/^(.+?)\s*:\s*(https?:\/\/\S.*)$/i);
    if (withUrl) {
      output.push(`- ${withUrl[1].trim()}`);
      continue;
    }

    const urlOnly = bullet.match(/^(https?:\/\/\S.*)$/i);
    if (urlOnly) {
      const url = urlOnly[1].trim();
      const rawName = url.split('?')[0].split('/').pop() || 'Attached file';
      output.push(`- ${decodeURIComponent(rawName)}`);
      continue;
    }

    // Ignore helper/instruction lines under attached-files section.
    continue;
  }

  return output.join('\n');
}

function extractAttachmentsFromQuestion(question: string): FileAttachment[] {
  const rows: FileAttachment[] = [];
  const lines = (question || '').split('\n');
  let inAttachedSection = false;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (inAttachedSection) break;
      continue;
    }
    if (/^\[?Attached files\]?\s*:?$/i.test(line)) {
      inAttachedSection = true;
      continue;
    }
    if (!inAttachedSection) continue;

    const bullet = line.replace(/^[-*]\s*/, '').trim();
    // The URL runs to end of line and may contain literal (unencoded) spaces —
    // blob SAS URLs keep the original filename's spaces, so \S+ would drop
    // any attachment whose name has a space in it.
    const withUrl = bullet.match(/^(.+?)\s*:\s*(https?:\/\/\S.*)$/i);
    if (withUrl) {
      rows.push({ name: withUrl[1].trim(), fileUrl: withUrl[2].trim(), size: 0, type: '' });
      continue;
    }

    const urlOnly = bullet.match(/^(https?:\/\/\S.*)$/i);
    if (!urlOnly) continue;

    const url = urlOnly[1].trim();
    const rawName = url.split('?')[0].split('/').pop() || 'Attached file';
    rows.push({ name: decodeURIComponent(rawName), fileUrl: url, size: 0, type: '' });
  }

  return rows;
}

function buildHistoryTitle(question: string): string {
  const normalized = normalizeAttachedFilesInQuestion(question || '');
  const firstLine = normalized
    .split('\n')
    .map(line => line.trim())
    .find(line => !!line && !/^\[?Attached files\]?\s*:?$/i.test(line) && !/^-\s+/.test(line)) || '';
  const title = firstLine || 'Untitled conversation';
  return title.length > 40 ? `${title.substring(0, 40).trimEnd()}...` : title;
}

// The backend sometimes serializes an empty file-name field as the literal
// string "[]" (or "null") instead of leaving it blank/null — without this
// guard that sentinel gets treated as a real filename and shown as an
// attachment chip on turns where no file actually exists.
function isRealFileName(value: string | null | undefined): boolean {
  const trimmed = (value || '').trim();
  if (!trimmed) return false;
  const lowered = trimmed.toLowerCase();
  return lowered !== '[]' && lowered !== 'null' && lowered !== 'undefined';
}

function entryToMessages(entry: ConversationEntry, pdfUrlsPool: string[] = []): ChatMessage[] {
  const msgs: ChatMessage[] = [];
  const extractedAttachments = extractAttachmentsFromQuestion(entry.question || '');
  const userAttachments: FileAttachment[] = [...extractedAttachments];
  if (userAttachments.length === 0 && isRealFileName(entry.pdfName)) {
    const fallbackUrl = pdfUrlsPool.length > 0 ? pdfUrlsPool.shift() : undefined;
    userAttachments.push({
      name: entry.pdfName.trim(),
      fileUrl: fallbackUrl,
      size: 0,
      type: '',
    });
  }
  const normalizedQuestion = normalizeAttachedFilesInQuestion(entry.question || '');
  const turnTimestamp = formatHistoryTimestamp(entry.created);

  msgs.push({
    id: `user-${entry.chatId}`,
    role: 'user',
    content: normalizedQuestion,
    timestamp: turnTimestamp,
    ...(userAttachments.length > 0 ? { metadata: { attachments: userAttachments } } : {}),
  });

  const pageURLs = Array.isArray(entry.pageURL) && entry.pageURL.length > 0 ? entry.pageURL : undefined;

  // Chart data: prefer the dedicated field, fall back to extracting from response text
  const chartJsData = entry.chartJsData || extractChartJsFromText(entry.response || '');

  const imageURLs = Array.isArray(entry.imageURL) && entry.imageURL.length > 0 ? entry.imageURL : undefined;

  const metadata: Record<string, any> = {
    chatId: entry.chatId,
    like: entry.like,
    disLike: entry.disLike,
    feedback: entry.feedback ?? '',
  };
  if (pageURLs) metadata.pageURL = pageURLs;
  if (chartJsData) metadata.chartJsData = chartJsData;
  if (imageURLs) metadata.imageURL = imageURLs;
  // Keep the aiToolName array as-is when it holds at least one real name so
  // its indices stay aligned with aiToolPayload/aiToolResponse.
  const rawToolNames = Array.isArray(entry.aiToolName) ? entry.aiToolName : [];
  const toolNames = rawToolNames.some(n => !isMeaninglessToolName(n))
    ? rawToolNames
    : parseAiToolField(entry.aI_Tool);
  if (toolNames.length > 0) {
    metadata.aiToolName = toolNames;
    metadata.aiToolPayload = entry.aiToolPayload ?? [];
    metadata.aiToolResponse = entry.aiToolResponse ?? [];
  }
  // Generated files → attachments, so ChatMessage renders the same
  // "Attachments" list below the response as it does for live turns.
  // Postgres-sourced rows carry only artifact_id — the portal viewer URL is
  // this app's own /artifacts/:guid route, so build it from our origin.
  const artifactAttachments: FileAttachment[] = [];
  for (const a of entry.artifacts ?? []) {
    const url = a.app_url || (a.artifact_id ? `${window.location.origin}/artifacts/${a.artifact_id}` : '');
    if (!url) continue;
    const fallbackName = decodeURIComponent(url.split('?')[0].split('/').pop() || '') || 'Generated file';
    artifactAttachments.push({ name: isRealFileName(a.filename) ? a.filename!.trim() : fallbackName, fileUrl: url, size: 0, type: '' });
  }
  if (artifactAttachments.length > 0) metadata.attachments = artifactAttachments;
  // Generate-artifact button below the latest assistant response —
  // ChatMessage reads this from the message's metadata.
  if (entry.canGenerateArtifact) metadata.canGenerateArtifact = true;

  msgs.push({
    id: `ai-${entry.chatId}`,
    role: 'assistant',
    content: entry.response,
    timestamp: turnTimestamp,
    metadata,
  });

  return msgs;
}

/* ── Public API ── */

export interface RecentHistoryPage {
  threads: ChatThread[];
  totalCount: number;
}

const EMPTY_PAGE: RecentHistoryPage = { threads: [], totalCount: 0 };

// How a recent-history listing is ordered. Omitted → by when each conversation
// started (the AMP API's default, which every existing caller relies on).
// 'lastAccessed' → the conversation the user most recently opened or added to
// first; the AMP API stamps a conversation when its history is loaded, so just
// reopening a chat moves it up.
export type RecentHistorySort = 'lastAccessed';

// The timestamp a listing is ordered by — the row's updatedAt and the time it
// shows. With 'lastAccessed' that is when the user last opened or added to the
// conversation (falling back to its start for an AMP API that predates the
// field); otherwise, as before, when the conversation started.
function recentHistoryTime(item: RecentHistoryItem, sortBy?: RecentHistorySort): string {
  const key = sortBy === 'lastAccessed' ? item.lastAccessed || item.created : item.created;
  return key || new Date().toISOString();
}

function mapRecentHistoryItem(item: RecentHistoryItem, sortBy?: RecentHistorySort): ChatThread {
  return {
    id: item.conversationId,
    title: buildHistoryTitle(item.question || ''),
    messages: [],
    createdAt: item.created || new Date().toISOString(),
    updatedAt: recentHistoryTime(item, sortBy),
    agentId: item.agentId || undefined,
    agentName: item.agentName || undefined,
    isVoice: !!item.isVoice,
    // Only when it says something the title (the first question) doesn't.
    lastQuestion: (item.turns ?? 0) > 1 && item.lastQuestion?.trim() ? item.lastQuestion.trim() : undefined,
  };
}

// searchText set → the backend returns EVERY matching conversation (no Limit/
// Offset applied); omitted → a page of `limit` items starting at `offset`,
// plus totalCount of the full unfiltered set — used to drive infinite scroll.
// kind 'voice' / 'text' → only conversations that were (or weren't) spoken —
// the AMP API works that out per conversation and pages over the result.
// sortBy 'lastAccessed' → most recently opened/added-to first (see
// RecentHistorySort); omitted → by when each conversation started.
export async function fetchRecentHistoryForAmp(
  channelId?: string,
  ampId?: string,
  searchText?: string,
  limit = 10,
  offset = 0,
  kind?: 'voice' | 'text',
  sortBy?: RecentHistorySort,
): Promise<RecentHistoryPage> {
  const headers = getAuthHeaders();
  if (!headers) return EMPTY_PAGE;

  try {
    const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/momentumamp/momentum-my-amp-recent-history`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ChannelId: channelId || '',
        AMPId: ampId?.startsWith('custom:') ? ampId.replace('custom:', '') : (ampId || ''),
        SearchText: searchText?.trim() || undefined,
        Kind: kind,
        SortBy: sortBy,
        Limit: limit,
        Offset: offset,
      }),
    });
    if (!res.ok) return EMPTY_PAGE;

    const json = await res.json();
    const items: RecentHistoryItem[] = json?.data ?? [];
    const totalCount: number = json?.totalCount ?? items.length;

    // Ordered here by the same key the server paged by, so a page never
    // interleaves on the client.
    const timeOf = (item: RecentHistoryItem) => new Date(recentHistoryTime(item, sortBy)).getTime();
    return {
      threads: items
        .sort((a, b) => timeOf(b) - timeOf(a))
        .map((item) => mapRecentHistoryItem(item, sortBy)),
      totalCount,
    };
  } catch {
    return EMPTY_PAGE;
  }
}

/**
 * Marks a conversation as opened now, so a 'lastAccessed' listing puts it first.
 * Only needed when a chat is shown without loading it through
 * fetchConversationDetail (which stamps it server-side). Best effort: a failure
 * only means the order isn't refreshed, so it never throws.
 */
export async function touchConversation(conversationId: string): Promise<void> {
  const headers = getAuthHeaders();
  if (!headers) return;
  try {
    await fetch(
      `${process.env.REACT_APP_Auth_Url}/api/momentumamp/conversations/${encodeURIComponent(conversationId)}/touch`,
      { method: 'POST', headers },
    );
  } catch {
    // Ordering only — nothing to recover.
  }
}

export async function fetchConversationDetail(
  conversationId: string,
): Promise<{ messages: ChatMessage[]; pdfUrls: string[]; isVoice?: boolean }> {
  const headers = getAuthHeaders();
  if (!headers) return { messages: [], pdfUrls: [] };

  try {
    const res = await fetch(
      `${process.env.REACT_APP_Auth_Url}/api/momentumamp/momentum-my-amp-history/${conversationId}`,
      { headers }
    );
    if (!res.ok) return { messages: [], pdfUrls: [] };

    const data: ConversationDetailResponse = await res.json();
    const entries = data.momentumMyAMPHistoryConversation || [];

    return {
      messages: entries.flatMap(entry => entryToMessages(entry, data.fileUrls || [])),
      pdfUrls: data.fileUrls || [],
      isVoice: !!data.isVoice,
    };
  } catch {
    return { messages: [], pdfUrls: [] };
  }
}

export interface ToolCallDetail {
  question: string;
  aiToolName: string[];
  aiToolPayload: string[];
  aiToolResponse: string[];
  // Formatted turn timestamp, '' when the backend sent no `created` — callers
  // fall back to their own time in that case.
  createdAt: string;
}

// Fetches the same conversation-history endpoint as fetchConversationDetail and
// picks out one turn's tool-call fields by chatId — used to back the chat page's
// "View Details" button for turns whose history entry hasn't been loaded yet
// (e.g. a response that just finished streaming in the current session).
export async function fetchToolCallDetail(conversationId: string, turnChatId: string): Promise<ToolCallDetail | null> {
  const headers = getAuthHeaders();
  if (!headers) return null;

  try {
    const res = await fetch(
      `${process.env.REACT_APP_Auth_Url}/api/momentumamp/momentum-my-amp-history/${conversationId}`,
      { headers }
    );
    if (!res.ok) return null;

    const data: ConversationDetailResponse = await res.json();
    const entry = (data.momentumMyAMPHistoryConversation || []).find(e => e.chatId === turnChatId);
    if (!entry) return null;

    const rawToolNames = Array.isArray(entry.aiToolName) ? entry.aiToolName : [];
    const toolNames = rawToolNames.some(n => !isMeaninglessToolName(n))
      ? rawToolNames
      : parseAiToolField(entry.aI_Tool);

    return {
      question: normalizeAttachedFilesInQuestion(entry.question || ''),
      aiToolName: toolNames,
      aiToolPayload: entry.aiToolPayload ?? [],
      aiToolResponse: entry.aiToolResponse ?? [],
      createdAt: formatHistoryTimestamp(entry.created),
    };
  } catch {
    return null;
  }
}

// Pulls the file URL out of a `{ fileUrl, fileName }` descriptor body, if the
// body is one. Returns null for anything that isn't such a descriptor.
function extractDescriptorUrl(body: string): string | null {
  try {
    const data = JSON.parse(body) as { fileUrl?: string; fileURL?: string; url?: string } | null;
    const url = (data?.fileUrl || data?.fileURL || data?.url || '').trim();
    return url || null;
  } catch {
    return null;
  }
}

// Fetches a conversation's file through the authenticated proxy endpoint
// (blob SAS URLs served directly in `fileUrl` are being phased out) and hands
// back a URL the caller can pass straight into FilePreviewModal or an <a>.
//
// The endpoint answers in one of two shapes:
//   - a JSON descriptor `{ fileUrl, fileName }` carrying a short-lived SAS URL.
//     That URL is returned as-is (plain https, nothing to revoke);
//   - the raw file bytes, which are wrapped in an object URL. Caller owns that
//     URL and must URL.revokeObjectURL it when done (only `blob:` URLs).
// Wrapping the JSON case in an object URL made the preview iframe render the
// descriptor text instead of the document.
export async function fetchConversationFile(conversationId: string, fileName: string): Promise<string | null> {
  const headers = getAuthHeaders();
  if (!headers || !conversationId || !fileName) return null;

  try {
    const params = new URLSearchParams({ conversationId, fileName });
    const res = await fetch(`${process.env.REACT_APP_Auth_Url}/api/MomentumAMP/file?${params}`, { headers });
    if (!res.ok) return null;

    const contentType = (res.headers.get('content-type') || '').toLowerCase();
    if (contentType.includes('json')) {
      return extractDescriptorUrl(await res.text());
    }

    const blob = await res.blob();
    // Some proxies send the descriptor without a JSON content-type — sniff a
    // small text-ish body before treating it as file bytes.
    const looksTextual = contentType === '' || contentType.startsWith('text/') || contentType.includes('octet-stream');
    if (looksTextual && blob.size < 8192) {
      const url = extractDescriptorUrl(await blob.text());
      if (url) return url;
    }
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/**
 * Fired on window after a chat is archived ({ detail: { conversationId } }), so every
 * history list that keeps its own copy (History/Search modals, channel & AMP recent
 * conversations) can drop it right away.
 */
export const CHAT_ARCHIVED_EVENT = 'chatConversationArchived';

/**
 * One-way archive (soft delete): the chat disappears from every history list; its
 * messages stay in the database. Throws with a user-facing message on failure.
 */
export async function archiveConversation(conversationId: string): Promise<void> {
  const headers = getAuthHeaders();
  if (!headers) throw new Error('Please sign in again to archive this chat.');
  const res = await fetch(
    `${process.env.REACT_APP_Auth_Url}/api/momentumamp/conversations/${encodeURIComponent(conversationId)}/archive`,
    { method: 'POST', headers },
  );
  if (res.status === 404) throw new Error('This chat could not be found.');
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    throw new Error(json?.message || 'Could not archive this chat. Please try again.');
  }
  window.dispatchEvent(new CustomEvent(CHAT_ARCHIVED_EVENT, { detail: { conversationId } }));
}
